import SeoFooter from "../components/SeoFooter";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { toast } from "sonner";
import { 
  Phone, Mail, MapPin, Clock, MessageSquare, Send, CheckCircle2, 
  Calendar, Zap, Settings, Shield, ArrowRight, Building2, MessageCircle
} from "lucide-react";
import ContactPopup from "../components/ContactPopup";
import PageTransition from "../components/PageTransition";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    // Simulate form submission (email)
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setSubmitted(true);
    setSubmitting(false);
    toast.success("Message sent! We'll get back to you within 2 hours.");
  };

  const whyCallUs = [
    {
      icon: Zap,
      title: "Last-Minute Bookings",
      description: "If you need a car within the next 4 hours, please call us directly for immediate assistance."
    },
    {
      icon: Settings,
      title: "Custom Itineraries",
      description: "Need multiple stops or a specific vehicle not listed? We can arrange it over the phone."
    },
    {
      icon: Shield,
      title: "Special Requirements",
      description: "From specific refreshments to VIP security protocols, we are happy to accommodate your unique needs."
    }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Limo Near Me",
    "image": "https://www.limonearme.com.au/limo-logo.jpg",
    "telephone": "+61481437772",
    "email": "info@limonearme.com.au",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "AU",
      "addressRegion": "Queensland"
    },
    "url": "https://www.limonearme.com.au",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        "opens": "00:00",
        "closes": "23:59",
        "description": "Bookings available 24/7"
      }
    ],
    "areaServed": [
      { "@type": "City", "name": "Brisbane" },
      { "@type": "City", "name": "Sydney" },
      { "@type": "City", "name": "Melbourne" },
      { "@type": "City", "name": "Gold Coast" },
      { "@type": "City", "name": "Cairns" },
      { "@type": "City", "name": "Perth" }
    ],
    "priceRange": "$$-$$$"
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Contact Us | Limo Near Me | 24/7 Chauffeur Service Australia</title>
          <meta name="description" content="Contact Limo Near Me for premium chauffeur services. Available 24/7 for bookings. Call +61 481 437 772 for urgent requests. Custom quotes for weddings, corporate events, and special occasions." />
          <meta name="keywords" content="contact limo service, book chauffeur Australia, limousine booking phone, 24/7 limo service, custom transport quote" />
          <link rel="canonical" href="https://www.limonearme.com.au/contact" />
          <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        </Helmet>

        <ContactPopup />

        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-black/5">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center">
                <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-10 w-auto" />
              </Link>
              <div className="hidden md:flex items-center gap-6">
                <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Home</Link>
                <Link to="/fleet" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Our Fleet</Link>
                <Link to="/about" className="text-sm text-muted-foreground hover:text-foreground transition-colors">About Us</Link>
                <Link to="/faq" className="text-sm text-muted-foreground hover:text-foreground transition-colors">FAQ</Link>
                <a href="tel:+61481437772" className="flex items-center gap-2 text-primary font-semibold">
                  <Phone className="h-4 w-4" />
                  +61 481 437 772
                </a>
                <Link to="/book">
                  <Button size="sm">Book Now</Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="pt-24 pb-16 bg-gradient-to-b from-gray-900 to-gray-800 text-white">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                We're Here to Take You There
              </h1>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Whether you have a question about a booking or need a tailored transport solution for a large event, our team is ready to assist.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Get in Touch Cards */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid md:grid-cols-3 gap-6 -mt-16">
              {/* Phone Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-xl p-8 shadow-lg border border-black/5 text-center"
              >
                <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center mx-auto mb-4">
                  <Phone className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">Phone</h3>
                <a href="tel:+61481437772" className="text-2xl text-primary font-bold hover:underline block mb-2">
                  +61 481 437 772
                </a>
                <p className="text-sm text-muted-foreground">
                  Available 24/7 for urgent bookings and chauffeur coordination
                </p>
              </motion.div>

              {/* Email Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-xl p-8 shadow-lg border border-black/5 text-center"
              >
                <div className="w-16 h-16 rounded-full bg-gray-900 flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">Email</h3>
                <a href="mailto:info@limonearme.com.au" className="text-primary font-semibold hover:underline block mb-2">
                  info@limonearme.com.au
                </a>
                <p className="text-sm text-muted-foreground">
                  We respond within 2 hours during business hours
                </p>
              </motion.div>

              {/* Hours Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-xl p-8 shadow-lg border border-black/5 text-center"
              >
                <div className="w-16 h-16 rounded-full bg-gray-900 flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">Business Hours</h3>
                <div className="text-sm">
                  <p className="font-semibold text-foreground">Bookings:</p>
                  <p className="text-muted-foreground mb-2">24 Hours / 7 Days</p>
                  <p className="font-semibold text-foreground">Office/Admin:</p>
                  <p className="text-muted-foreground">Mon–Fri, 9:00 AM – 5:00 PM</p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Request a Custom Quote Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Form Side */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className="inline-flex items-center gap-2 bg-primary/10 rounded-full px-4 py-2 mb-4">
                  <Calendar className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium text-primary">Custom Quote</span>
                </div>
                <h2 className="text-3xl font-bold mb-4">Request a Custom Quote</h2>
                <p className="text-muted-foreground mb-8">
                  Planning something special? If you require transport for a wedding, a full-day corporate roadshow, or a multi-car event, please send us a message below. One of our coordinators will get back to you within 2 hours.
                </p>
                
                {submitted ? (
                  <div className="bg-green-50 rounded-xl p-8 text-center border border-green-200">
                    <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Message Sent!</h3>
                    <p className="text-muted-foreground mb-4">
                      Thank you for reaching out. One of our coordinators will get back to you within 2 hours.
                    </p>
                    <Button onClick={() => setSubmitted(false)} variant="outline">
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Name *</label>
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Your full name"
                          required
                          className="h-12"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Phone *</label>
                        <Input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="0400 000 000"
                          required
                          className="h-12"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Email</label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="your@email.com"
                        className="h-12"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Your Message (Tell us about your event) *</label>
                      <Textarea
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Please describe your event, dates, number of guests, and any special requirements..."
                        rows={5}
                        required
                      />
                    </div>
                    <Button type="submit" size="lg" className="w-full h-12" disabled={submitting}>
                      {submitting ? (
                        "Sending..."
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" />
                          Submit Request
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </motion.div>

              {/* Why Call Us Side */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl font-bold mb-4">Why Call Us Directly?</h2>
                <p className="text-muted-foreground mb-8">
                  While our online booking system is fast, we love speaking with our clients to ensure every detail is perfect.
                </p>

                <div className="space-y-6">
                  {whyCallUs.map((item, index) => (
                    <motion.div
                      key={item.title}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex gap-4 p-6 bg-white rounded-xl border border-black/5 hover:shadow-md transition-shadow"
                    >
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <item.icon className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-1">{item.title}</h3>
                        <p className="text-muted-foreground text-sm">{item.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Call Now CTA */}
                <div className="mt-8 p-6 bg-gray-900 rounded-xl text-white">
                  <h3 className="font-semibold text-lg mb-2">Need a Car in the Next 4 Hours?</h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Skip the form and call us directly for immediate assistance.
                  </p>
                  <a href="tel:+61481437772">
                    <Button size="lg" variant="secondary" className="w-full">
                      <Phone className="mr-2 h-4 w-4" />
                      Call Now: +61 481 437 772
                    </Button>
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Our Service Areas</h2>
              <p className="text-muted-foreground">
                Premium chauffeur services across Australia's major cities
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {[
                { city: "Brisbane", link: "/chauffeur/brisbane" },
                { city: "Gold Coast", link: "/chauffeur/gold-coast" },
                { city: "Sydney", link: "/chauffeur/sydney" },
                { city: "Melbourne", link: "/chauffeur/melbourne" },
                { city: "Cairns", link: "/chauffeur/cairns" },
                { city: "Perth", link: "/chauffeur/perth" }
              ].map((item) => (
                <Link 
                  key={item.city}
                  to={item.link}
                  className="flex items-center justify-center gap-2 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors text-center"
                >
                  <MapPin className="h-4 w-4 text-primary" />
                  <span className="font-medium">{item.city}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <MessageSquare className="h-12 w-12 mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">Ready to Experience Luxury Travel?</h2>
            <p className="text-primary-foreground/80 mb-8">
              Book online in minutes or speak with our team for a personalized experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book">
                <Button size="lg" variant="secondary" className="rounded-full px-8">
                  Book Online Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a href="tel:+61481437772">
                <Button size="lg" variant="outline" className="rounded-full px-8 border-white/30 text-white hover:bg-white/10">
                  <Phone className="h-4 w-4 mr-2" />
                  Call +61 481 437 772
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
      <SeoFooter />
      </div>
    </PageTransition>
  );
};

export default ContactPage;
